// notify.service.ts
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, inject } from '@angular/core';
import { ToastContainerComponent } from '@shared/components/toast-container/toast-container';
import { ToastOptions, ToastPosition, ToastRef } from '@shared/models/toast.model';

function uuid(): string {
    return (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`);
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
    private overlay = inject(Overlay);
    private overlayRef?: OverlayRef;
    private container?: ToastContainerComponent;

    private toasts: ToastRef[] = [];
    private timers = new Map<string, number>();

    show(message: string, options: ToastOptions = {}): string {
        this.ensureOverlay(options);

        const ref: ToastRef = {
            id: uuid(),
            message,
            createdAt: Date.now(),
            options: {
                durationMs: options.durationMs ?? 3500,
                type: options.type ?? 'info',
                dismissible: options.dismissible ?? true,
                title: options.title,
                actionText: options.actionText,
                onAction: options.onAction,
            },
        };

        // newest on top
        this.toasts = [ref, ...this.toasts].slice(0, 5); // cap stack size
        this.container!.setToasts(this.toasts);

        if (ref.options.durationMs > 0) {
            const handle = window.setTimeout(() => this.dismiss(ref.id), ref.options.durationMs);
            this.timers.set(ref.id, handle);
        }

        return ref.id;
    }

    success(message: string, options: ToastOptions = {}) {
        return this.show(message, { ...options, type: 'success' });
    }
    info(message: string, options: ToastOptions = {}) {
        return this.show(message, { ...options, type: 'info' });
    }
    warning(message: string, options: ToastOptions = {}) {
        return this.show(message, { ...options, type: 'warning' });
    }
    error(message: string, options: ToastOptions = {}) {
        return this.show(message, { ...options, type: 'error', durationMs: options.durationMs ?? 7000 });
    }

    dismiss(id: string) {
        const t = this.timers.get(id);
        if (t) {
            clearTimeout(t);
            this.timers.delete(id);
        }

        const next = this.toasts.filter(x => x.id !== id);
        this.toasts = next;
        this.container?.setToasts(this.toasts);

        // clean up overlay when empty
        if (this.toasts.length === 0) this.disposeOverlay();
    }

    clearAll() {
        for (const t of this.timers.values()) clearTimeout(t);
        this.timers.clear();
        this.toasts = [];
        this.container?.setToasts([]);
        this.disposeOverlay();
    }

    private ensureOverlay(options?: ToastOptions) {
        const position = options?.position ?? 'top-right';
        const strategy = this.buildPositionStrategy(position);

        if (this.overlayRef && this.container) {
            this.overlayRef.updatePositionStrategy(strategy);
            this.overlayRef.updatePosition();
            return;
        }

        this.overlayRef = this.overlay.create({
            positionStrategy: strategy,
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.noop(),
        });

        const portal = new ComponentPortal(ToastContainerComponent);
        const componentRef = this.overlayRef.attach(portal);
        this.container = componentRef.instance;

        this.container.onDismiss = (id) => this.dismiss(id);
        this.container.onAction = (id) => {
            const toast = this.toasts.find(t => t.id === id);
            toast?.options.onAction?.();
            // optional: dismiss on action
            this.dismiss(id);
        };
    }

    private disposeOverlay() {
        this.overlayRef?.dispose();
        this.overlayRef = undefined;
        this.container = undefined;
    }

    private buildPositionStrategy(position: ToastPosition) {
        const overlay = this.overlay.position().global();
        const offset = '12px';

        switch (position) {
            case 'top-left':
                return overlay.top(offset).left(offset);
            case 'top-right':
                return overlay.top(offset).right(offset);
            case 'bottom-left':
                return overlay.bottom(offset).left(offset);
            case 'bottom-right':
                return overlay.bottom(offset).right(offset);
        }
    }
}